/**
 * Workspace · Transfers ledger (S37).
 *
 * Thin presentation over `cashMovements.list({ kind: 'transfer' })`. KPI
 * tiles + filter chips + table of `<TransferRow>` rows. The subtype pill
 * on each row is derived from `(fromAccount.type, toAccount.type)` via
 * `classifySubtype` (BANK→BANK / BANK→CASH / CASH→BANK / BANK→CARD).
 *
 * KPI tiles are server-aggregated via `cashMovements.transferStats` (the
 * current calendar month). The listing keeps showing the full window for
 * scrolling.
 *
 * @see prototype/cash/transfers.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.7
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  ArrowRight,
  CreditCard,
  Download,
  Plus,
  Search,
  Wallet,
} from "lucide-react";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  StatTile,
  DataTableCard,
  ListPagination,
} from "../../../../features/master-list/index.ts";
import {
  TransferRow,
  classifySubtype,
  type BankAccountType,
  type TransferSubtype,
} from "../../../../features/transfers/index.ts";
import { paiseToInrShort, formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/transfers/")({
  component: TransfersPage,
});

function TransfersPage() {
  // Need account lookup for type → subtype derivation.
  const accountsQ = trpc.bankAccounts.list.useQuery({ includeArchived: true });
  const listQ = trpc.cashMovements.list.useQuery({ kind: "transfer", limit: 100 });
  // Server-aggregated KPI tiles — defaults to the current UTC month window.
  const statsQ = trpc.cashMovements.transferStats.useQuery({});

  const isLoading = accountsQ.isLoading || listQ.isLoading;
  const firstError = accountsQ.error ?? listQ.error;

  if (isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Transfers" }]}>
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading transfers…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }
  if (firstError || !accountsQ.data || !listQ.data) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Transfers" }]}>
        <Alert
          intent="bad"
          title="Couldn't load transfers"
          description={firstError?.message ?? "Missing data"}
        />
      </WorkspaceShell>
    );
  }

  return (
    <TransfersBody
      accounts={accountsQ.data.rows}
      rows={listQ.data.rows}
      stats={statsQ.data ?? null}
    />
  );
}
interface TransferStatsBucketWire {
  paise: string;
  count: number;
}

interface TransferStatsWire {
  totalMovedPaise: string;
  totalMovedCount: number;
  bankToBank: TransferStatsBucketWire;
  bankToCash: TransferStatsBucketWire;
  cashToBank: TransferStatsBucketWire;
  bankToCard: TransferStatsBucketWire;
}

interface AccountRow {
  id: string;
  name: string;
  type: BankAccountType;
}

interface MovementRow {
  id: string;
  kind: string;
  fromAccountId: string | null;
  fromAccountName: string | null;
  toAccountId: string | null;
  toAccountName: string | null;
  amountPaise: string;
  valueDate: string;
  narration: string;
  createdAt: string;
}

interface EnrichedRow {
  id: string;
  valueDate: string;
  from: { id: string; name: string; type: BankAccountType };
  to: { id: string; name: string; type: BankAccountType };
  amountPaise: string;
  narration: string;
  subtype: TransferSubtype | null;
}

function TransfersBody({
  accounts,
  rows,
  stats,
}: {
  accounts: ReadonlyArray<AccountRow>;
  rows: ReadonlyArray<MovementRow>;
  stats: TransferStatsWire | null;
}) {
  const [query, setQuery] = useState("");
  const [subtypeFilter, setSubtypeFilter] = useState<TransferSubtype | "all">("all");

  const accountMap = useMemo(() => {
    const map = new Map<string, AccountRow>();
    for (const a of accounts) map.set(a.id, a);
    return map;
  }, [accounts]);

  const enriched: ReadonlyArray<EnrichedRow> = useMemo(() => {
    const list: EnrichedRow[] = [];
    for (const r of rows) {
      if (!r.fromAccountId || !r.toAccountId) continue;
      const from = accountMap.get(r.fromAccountId);
      const to = accountMap.get(r.toAccountId);
      if (!from || !to) continue;
      list.push({
        id: r.id,
        valueDate: r.valueDate,
        from: { id: from.id, name: from.name, type: from.type },
        to: { id: to.id, name: to.name, type: to.type },
        amountPaise: r.amountPaise,
        narration: r.narration,
        subtype: classifySubtype(from.type, to.type),
      });
    }
    return list;
  }, [rows, accountMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enriched.filter((r) => {
      if (subtypeFilter !== "all" && r.subtype !== subtypeFilter) return false;
      if (!q) return true;
      return (
        r.narration.toLowerCase().includes(q) ||
        r.from.name.toLowerCase().includes(q) ||
        r.to.name.toLowerCase().includes(q)
      );
    });
  }, [enriched, subtypeFilter, query]);

  // Footer-1 stats over the currently-VISIBLE (filtered) rows — distinct
  // from the server-aggregated "this month" KPI tiles above.
  const visibleStats = useMemo(() => computeVisibleStats(filtered), [filtered]);

  // Server stats reflect the current UTC month; the listing below still
  // walks the full enriched list for scrolling. The two windows diverge —
  // tile copy says "this month" to keep the wording honest.
  const tileStats = {
    totalPaise: stats === null ? 0n : BigInt(stats.totalMovedPaise),
    totalCount: stats?.totalMovedCount ?? 0,
    bankToBankPaise: stats === null ? 0n : BigInt(stats.bankToBank.paise),
    bankToCashPaise: stats === null ? 0n : BigInt(stats.bankToCash.paise),
    bankToCardPaise: stats === null ? 0n : BigInt(stats.bankToCard.paise),
    bankToBankCount: stats?.bankToBank.count ?? 0,
    bankToCashCount: stats?.bankToCash.count ?? 0,
    bankToCardCount: stats?.bankToCard.count ?? 0,
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Transfers" }]}>
      <div className="flex flex-col gap-5">
        {/* Page header */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Transfers
            </h1>
            <p className="mt-1 text-xs text-muted">
              {enriched.length} {enriched.length === 1 ? "transfer" : "transfers"} · own-to-own
              money movements
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button intent="ghost" size="md" className="border border-line" disabled>
              <Download size={13} strokeWidth={2} aria-hidden />
              Export
            </Button>
            <Button asChild intent="primary" size="md">
              <Link to="/cash/transfers/new">
                <Plus size={13} strokeWidth={2.4} aria-hidden />
                New transfer
              </Link>
            </Button>
          </div>
        </header>

        {/* KPI strip */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
          aria-label="Transfer summary"
        >
          <StatTile
            label="Moved this month"
            value={paiseToInrShort(tileStats.totalPaise)}
            meta={`${tileStats.totalCount} ${tileStats.totalCount === 1 ? "transfer" : "transfers"} · current month`}
            tone="sky"
            icon={ArrowLeftRight}
          />
          <StatTile
            label="Bank → Cash top-ups"
            value={paiseToInrShort(tileStats.bankToCashPaise)}
            meta={`${tileStats.bankToCashCount} ${tileStats.bankToCashCount === 1 ? "transfer" : "transfers"}`}
            tone="ok"
            icon={Wallet}
          />
          <StatTile
            label="Bank → Card settlements"
            value={paiseToInrShort(tileStats.bankToCardPaise)}
            meta={`${tileStats.bankToCardCount} ${tileStats.bankToCardCount === 1 ? "settlement" : "settlements"}`}
            tone="warn"
            icon={CreditCard}
          />
          <StatTile
            label="Inter-bank · Bank → Bank"
            value={paiseToInrShort(tileStats.bankToBankPaise)}
            meta={`${tileStats.bankToBankCount} ${tileStats.bankToBankCount === 1 ? "transfer" : "transfers"}`}
            tone="muted"
            icon={ArrowRight}
          />
        </section>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2">
          <SubtypeFilterPill
            active={subtypeFilter === "all"}
            onClick={() => setSubtypeFilter("all")}
          >
            All
          </SubtypeFilterPill>
          <SubtypeFilterPill
            active={subtypeFilter === "bank_to_bank"}
            onClick={() => setSubtypeFilter("bank_to_bank")}
          >
            BANK→BANK
          </SubtypeFilterPill>
          <SubtypeFilterPill
            active={subtypeFilter === "bank_to_cash"}
            onClick={() => setSubtypeFilter("bank_to_cash")}
          >
            BANK→CASH
          </SubtypeFilterPill>
          <SubtypeFilterPill
            active={subtypeFilter === "cash_to_bank"}
            onClick={() => setSubtypeFilter("cash_to_bank")}
          >
            CASH→BANK
          </SubtypeFilterPill>
          <SubtypeFilterPill
            active={subtypeFilter === "bank_to_card"}
            onClick={() => setSubtypeFilter("bank_to_card")}
          >
            BANK→CARD
          </SubtypeFilterPill>
          <label className="inline-flex items-center gap-1.5 bg-paper border border-line rounded-sm px-2 py-1 flex-1 min-w-[200px] max-w-[320px] focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100">
            <Search size={12} className="text-muted" strokeWidth={2} aria-hidden />
            <input
              type="search"
              placeholder="Search amount, narration, account…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search transfers"
              className="flex-1 bg-transparent border-0 outline-none text-xs text-ink placeholder:text-muted"
            />
          </label>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-muted">
                {enriched.length === 0
                  ? "No transfers recorded yet. Use “New transfer” to move money between your own accounts."
                  : "No transfers match these filters."}
              </p>
            </CardBody>
          </Card>
        ) : (
          <DataTableCard
            footer={[
              { label: "Visible", value: filtered.length },
              { label: "Page total", value: paiseToInrShort(visibleStats.sumPaise) },
              { label: "Avg", value: paiseToInrShort(visibleStats.avgPaise) },
              ...(visibleStats.topSubtype
                ? [
                    {
                      label: "Top type",
                      value: `${visibleStats.topSubtype.label} · ${visibleStats.topSubtype.count}`,
                    },
                  ]
                : []),
              ...(visibleStats.dateRange ? [{ label: "Dates", value: visibleStats.dateRange }] : []),
            ]}
            pagination={
              <ListPagination
                pageNumber={1}
                showingFrom={1}
                showingTo={filtered.length}
                total={filtered.length}
                sortLabel="sorted by value date (newest first)"
                hasPrev={false}
                hasNext={false}
                onPrev={() => {}}
                onNext={() => {}}
              />
            }
          >
            <table className="w-full text-sm">
              <thead className="bg-bg">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-line">
                  <th className="px-3 py-2.5 font-semibold">Date</th>
                  <th className="px-3 py-2.5 font-semibold">Transfer</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Amount</th>
                  <th className="px-3 py-2.5 font-semibold">Reference</th>
                  <th className="px-3 py-2.5 font-semibold">Subtype</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) =>
                  r.subtype ? (
                    <TransferRow
                      key={r.id}
                      id={r.id}
                      valueDate={formatDate(r.valueDate)}
                      fromAccount={{ name: r.from.name, type: r.from.type }}
                      toAccount={{ name: r.to.name, type: r.to.type }}
                      amountPaise={r.amountPaise}
                      narration={r.narration}
                      subtype={r.subtype}
                    />
                  ) : null,
                )}
              </tbody>
            </table>
          </DataTableCard>
        )}
      </div>
    </WorkspaceShell>
  );
}

/** Visible-set aggregates for the transfers stats footer. */
function computeVisibleStats(rows: ReadonlyArray<EnrichedRow>): {
  sumPaise: bigint;
  avgPaise: bigint;
  topSubtype: { label: string; count: number } | null;
  dateRange: string | null;
} {
  if (rows.length === 0) {
    return { sumPaise: 0n, avgPaise: 0n, topSubtype: null, dateRange: null };
  }
  let sum = 0n;
  let min = rows[0]!.valueDate;
  let max = rows[0]!.valueDate;
  const subtypeCounts = new Map<TransferSubtype, number>();
  for (const r of rows) {
    sum += BigInt(r.amountPaise);
    if (r.valueDate < min) min = r.valueDate;
    if (r.valueDate > max) max = r.valueDate;
    if (r.subtype) subtypeCounts.set(r.subtype, (subtypeCounts.get(r.subtype) ?? 0) + 1);
  }
  let topSubtype: { label: string; count: number } | null = null;
  for (const [key, count] of subtypeCounts) {
    if (!topSubtype || count > topSubtype.count) {
      topSubtype = { label: key.replace(/_to_/g, "→").toUpperCase(), count };
    }
  }
  return {
    sumPaise: sum,
    avgPaise: sum / BigInt(rows.length),
    topSubtype,
    dateRange: min === max ? formatDate(min) : `${formatDate(min)} → ${formatDate(max)}`,
  };
}

function SubtypeFilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center h-7 px-2.5 rounded-sm border text-xs font-semibold transition-colors font-mono uppercase tracking-wider " +
        (active
          ? "bg-sky-50 border-sky-200 text-sky-700"
          : "bg-paper border-line text-ink-2 hover:bg-raised")
      }
    >
      {children}
    </button>
  );
}
